<template>
  <div class="image-grid-container">
    <!-- Grid Layout -->
    <div
      class="image-grid"
      :class="{ 'image-grid--single': images.length === 1 }"
      :style="maxGridColumns ? { '--max-grid-columns': maxGridColumns } : {}"
    >
      <!-- Main Image (Left) -->
      <div
        v-for="(image, i) in imagesToDisplay"
        :key="i + '_imageCarouselGridImage'"
        class="image-grid__image-container"
        :class="{ 'image-grid__main-image-container': i === 0 }"
        @click="openCarousel(i)"
      >
        <q-img
          :src="image"
          class="image-grid__image"
          fit="cover"
          :class="{ '--hover-effect': hoverEffect }"
          :style="[i > 0 ? { 'max-height': '200px' } : {}]"
        />
        <div
          v-if="i === imagesToDisplay.length - 1 && images.length > 1"
          class="overlay"
        >
          <q-btn
            flat
            color="background-very-very-light"
            text-color="white"
            @click.stop="openCarousel(i)"
            no-caps
          >
            <q-icon name="sym_r_photo_library" class="q-mr-sm" />
            <div class="text-weight-bold">
              View all {{ images.length }} images
            </div>
          </q-btn>
        </div>
      </div>
    </div>

    <!-- Fullscreen Carousel Dialog -->
    <q-dialog v-model="showCarousel" fullscreen>
      <q-card class="fullscreen-carousel__container">
        <!-- Close Button -->
        <q-btn
          round
          dense
          icon="sym_r_close"
          color="background-very-very-light"
          class="absolute-top-right q-ma-md --bordered"
          style="z-index: 1000"
          @click="showCarousel = false"
        />

        <!-- Carousel -->
        <q-carousel
          v-model="currentSlide"
          animated
          swipeable
          infinite
          thumbnails
          arrows
          class="bg-background full-height"
        >
          <q-carousel-slide
            v-for="(image, index) in images"
            :key="index"
            :name="index"
            class="flex flex-center"
            :img-src="image"
          />
        </q-carousel>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
export default {
  name: "ImageCarouselGrid",
  props: {
    images: {
      type: Array,
      required: true
    },
    hoverEffect: {
      type: Boolean,
      default: false
    },
    maxGridColumns: {
      type: Number,
      default: null,
      validator: value =>
        value === null || (value > 0 && Number.isInteger(value))
    }
  },
  data() {
    return {
      showCarousel: false,
      currentSlide: 0
    };
  },
  computed: {
    imagesToDisplay() {
      if (this.maxGridColumns !== null) {
        // Calculate based on maxGridColumns: main image takes 2x2 grid, others take 1 cell each
        const totalCells = this.maxGridColumns * 2;
        const mainImageCells = 4; // 2x2 grid
        const remainingCells = totalCells - mainImageCells;
        return this.images.slice(0, 1 + remainingCells);
      }
      return this.images.slice(0, this.windowWidth <= 1025 ? 3 : 5);
    }
  },
  methods: {
    openCarousel(index) {
      this.currentSlide = index;
      this.showCarousel = true;
    }
  }
};
</script>

<style scoped lang="scss">
.image-grid-container {
  width: 100%;
  margin: 0 auto;

  .image-grid {
    display: grid;
    grid-template-columns: repeat(var(--max-grid-columns, 4), 1fr);
    grid-template-rows: repeat(2, 1fr);
    grid-column-gap: $space-md;
    grid-row-gap: $space-md;

    @include screen-md {
      grid-template-columns: repeat(var(--max-grid-columns, 3), 1fr);
    }
    @include screen-custom(560) {
      grid-template-columns: repeat(var(--max-grid-columns, 2), 1fr);
    }

    .image-grid__image-container {
      position: relative;
    }
    .image-grid__main-image-container {
      grid-area: 1 / 1 / 3 / 3;
    }

    .image-grid__image {
      border-radius: 12px;
      cursor: pointer;
      background: color(background);

      &.--hover-effect {
        @include transition();

        &:hover {
          transform: scale(1.02);
        }
      }
    }
  }

  .image-grid--single {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;

    .image-grid__main-image-container {
      grid-area: 1 / 1 / 2 / 2;
    }
  }
}

.image-with-overlay {
  cursor: pointer;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: background 0.3s ease;
  cursor: pointer;
  border-radius: 12px;
}

.overlay:hover {
  background: rgba(0, 0, 0, 0.57);
}

.fullscreen-carousel__container {
  max-width: none;
  width: 100%;
  height: 100%;
  margin: 0 $space-lg;

  @include screen-sm {
    max-height: none;
    height: 100dvh;
    margin: 0;
    border-radius: 0 !important;
  }
}

/* Responsive Design */
/*@media (max-width: 768px) {
  .image-grid {
    grid-template-columns: 1fr;
    height: auto;
  }

  .main-image {
    height: 300px;
  }

  .stacked-images {
    flex-direction: row;
  }

  .stacked-image {
    height: 150px;
  }
}*/
</style>
